import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LoadingRing from 'ui/atoms/loading-ring';
import { useInvestorSelector } from 'apps/investor/resources/investor/hooks';
import { extractNaturalAndLegalFromPerson } from 'core/api/conversions';
import WideContent from 'core/layout/content/wide-content';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import Translate from 'ui/atoms/translate';
import BoxedChangeEmailAddress from './change-email-address';
import BoxedChangePassword from './change-password';
import BoxedChangeCommunicationSettings from './change-communication-settings';
import BoxedWallets from './wallets';
import UserDataView from './user-data-view';
import useApiCall from 'hooks/use-api-call';
import { Investor, InvestorsApi } from 'api';

const InvestorSettingsPage = () => {
  const { investor, loading } = useInvestorSelector();
  const [investorDetails, setInvestorDetails] = useState<Investor>();

  const { withApi, makeAuthenticatedApi } = useApiCall();
  const investorsApi: InvestorsApi = useMemo(() => makeAuthenticatedApi(InvestorsApi), [makeAuthenticatedApi]);

  const { naturalPerson } = extractNaturalAndLegalFromPerson(investorDetails?.person);

  const fetchInvestorDetails = useCallback(() => {
    withApi(async () => {
      const investorDetails = await investorsApi.investorsRetrieve({ id: investor?.id || '' });
      setInvestorDetails(investorDetails);
    });
  }, [investor?.id]);

  useEffect(() => {
    fetchInvestorDetails();
  }, [withApi, investorsApi, investor?.id]);

  if (loading || !investorDetails) {
    return <LoadingRing />;
  }

  return (
    <WideContent title={<Translate name="dashboardInvestorSettings.title" />}>
      <UserDataView investor={investorDetails} fetchInvestorDetails={fetchInvestorDetails} />
      <BoxedWallets />
      <BoxedChangePassword naturalPerson={naturalPerson || undefined} />
      <BoxedChangeEmailAddress />
      <BoxedChangeCommunicationSettings investor={investorDetails} />
    </WideContent>
  );
};

export default WithAuthenticatedPage(InvestorSettingsPage, AppType.INVESTOR);
